// Management reporting & BI dashboard (/office/reporting). The platform's live data turned
// into decisions: portfolio health, commercial, operations and compliance, each its OWN
// independently-refreshable panel backed by its own server-side aggregation endpoint
// (GET /api/reporting/portfolio-health | commercial | operations | compliance-summary), with
// its own "last updated" stamp. A global Refresh re-fetches every panel. The from/to control
// windows the operations panel. Every figure is live (no invented metrics). Money is
// display-only.
//
// The Monthly revenue panel (Session AKF, 18 Aug 2026, Director decision) folds in the two parts
// of the old standalone /office/reports "Monthly report" page worth keeping -- the month-scoped
// PDF export and the Revenue tile -- per the original nav audit's own recommendation. The rest of
// that page's tiles (jobs completed, first-time fix, LOLER/PPM compliance, breakdowns, at-risk
// clients, renewals) are dropped, not carried over: every one is already shown elsewhere on this
// page (PortfolioHealthPanelView, PpmCompliancePanelView, OperationsPanelView), so restating them
// here would be a second, differently-windowed copy of a figure this page already owns. /office/
// reports itself now redirects here (src/App.tsx) rather than 404ing.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type {
  MorningActionItem,
  NpsSummary,
  PortfolioHealthPanel,
  CommercialPanel,
  OperationsPanel,
  ComplianceSummaryPanel,
  ReportingPpmComplianceResult,
  ProfitabilityPanel,
  UtilisationPanel,
  UtilisationRow,
  MonthlyReport,
} from "../../lib/api";
import { RecordPicker } from "../../components/RecordPicker";
import { ACTION_BADGE, ACTION_LABEL } from "../../lib/actionCategories";
import { COST_FLOOR_TONE } from "../../lib/costFloorTone";
import { StatusPill, toneTextClass } from "../../components/StatusPill";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { formatDate, formatTime } from "../../lib/format";
import { useAuth } from "../../lib/auth";
import { KpiTileSet } from "./KpiTileSet";

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

// The Revenue tile is an actual monthly total worth showing to the penny, unlike the whole-pound
// gbp() above (used for the larger, approximate ARR/MRR-style figures on the other panels).
function gbpExact(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function currentReportMonth(): string {
  return new Date().toISOString().slice(0, 7); // UTC YYYY-MM, matching the server
}

function pct(n: number): string {
  return `${n}%`;
}

const STAGE_LABEL: Record<string, string> = {
  new: "New",
  contacted: "Contacted",
  quoting: "Quoting",
  quote_sent: "Quote sent",
  negotiating: "Negotiating",
};

// Session ANG. These are plural TILE headings ("Call-outs"), which is why this map exists beside
// the shared jobTypeLabel rather than reading it. The fallback where a key is missing is the RAW
// KEY, so this map had been printing a tile headed "condition_report" since Session ALW widened
// reporting.ts's JOB_TYPES without widening this -- an inherited defect ANG found and fixed rather
// than leaving, since it is the same one line as its own.
const JOB_TYPE_LABEL: Record<string, string> = {
  service: "Service (PPM)",
  callout: "Call-outs",
  loler: "LOLER",
  repair: "Repair",
  condition_report: "Condition reports",
  site_inspection: "Site inspections",
  tech_visit: "Tech visits",
  insurance_check: "Insurance checks",
  safed_test: "SAFed tests",
};

function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function Tile({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: "positive" | "alert" | "neutral" }) {
  const valueColor = tone === "positive" ? "text-ele-green" : tone === "alert" ? "text-ele-danger" : "text-ele-strong";
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">{label}</div>
      <div className={`mt-2 font-heading text-2xl ${valueColor}`}>{value}</div>
      {hint ? <div className="mt-1 text-xs text-ele-body opacity-60">{hint}</div> : null}
    </div>
  );
}

// A small per-panel "last updated" stamp (the brief: every panel carries its own).
function UpdatedStamp({ iso }: { iso: string | null }) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return <span className="font-menu text-[11px] uppercase tracking-wide text-ele-body opacity-50">Updated {formatTime(d)}</span>;
}

function Panel({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface-soft p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h2 className="font-heading text-xl text-ele-blue">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function PanelStates({ error, loading, what }: { error: boolean; loading: boolean; what: string }) {
  if (error)
    return (
      <p role="alert" className="text-sm text-ele-danger">
        Could not load {what}.
      </p>
    );
  if (loading) return <Loader label={`Loading ${what}...`} shape="inline" />;
  return null;
}

// Extract the quote id from a quote_follow_up action id (`quote_follow_up:<quoteId>`).
function quoteIdFromActionId(actionId: string): string {
  return actionId.slice("quote_follow_up:".length);
}

// The panel shows this many; the server sends this many (the list behind it can run to a thousand) with the TRUE total.
const MORNING_TOP = 25;

function MorningActionsPanel({ refreshKey }: { refreshKey: number }) {
  const [items, setItems] = useState<MorningActionItem[] | null>(null);
  // The count of everything on the list, from the server: never the length of the top items it sent (Session SC2B).
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [snoozing, setSnoozing] = useState<Set<string>>(new Set());
  const [snoozeError, setSnoozeError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setError(false);
    api.reporting
      .morningActionsTop(MORNING_TOP)
      .then((d) => {
        if (!active) return;
        setItems(d.items);
        setTotal(d.total);
        setUpdatedAt(new Date().toISOString());
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  // Snooze a quote follow-up for 7 days, then drop it from the list so it disappears for the window.
  const markFollowedUp = useCallback((actionId: string) => {
    const quoteId = quoteIdFromActionId(actionId);
    setSnoozing((s) => new Set(s).add(actionId));
    setSnoozeError(null);
    api.quotes
      .markFollowedUp(quoteId)
      .then(() => {
        setItems((prev) => (prev ? prev.filter((i) => i.id !== actionId) : prev));
        setTotal((t) => Math.max(0, t - 1));
        // The list was cut to the top items, so read it again quietly to bring the next one up into the gap.
        api.reporting
          .morningActionsTop(MORNING_TOP)
          .then((d) => {
            setItems(d.items);
            setTotal(d.total);
          })
          .catch(() => undefined);
      })
      .catch(() => setSnoozeError("Could not mark this as followed up. Please try again."))
      .finally(() =>
        setSnoozing((s) => {
          const next = new Set(s);
          next.delete(actionId);
          return next;
        }),
      );
  }, []);

  return (
    <Panel
      title="Today's priorities"
      action={
        <div className="flex items-center gap-3">
          {items ? <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{total} item{total === 1 ? "" : "s"}</span> : null}
          <UpdatedStamp iso={updatedAt} />
        </div>
      }
    >
      {error || items === null ? (
        <PanelStates error={error} loading={items === null} what="today's priorities" />
      ) : total === 0 ? (
        <p className="text-sm text-ele-green">Nothing urgent. The portfolio is clear.</p>
      ) : (
        <>
          {snoozeError && (
            <p role="alert" className="mb-2 text-xs text-ele-danger">
              {snoozeError}
            </p>
          )}
          <ul className="divide-y divide-ele-purple-accent/50">
            {items.map((a) => (
              <li key={a.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2">
                <span className={`mt-0.5 max-w-full shrink-0 rounded-full px-2 py-0.5 font-menu text-[10px] font-bold uppercase tracking-wide ${ACTION_BADGE[a.category]}`}>
                  {ACTION_LABEL[a.category]}
                </span>
                {/* min-w-[9rem] with flex-wrap on the row (Session BDY): the badge and the two buttons are shrink-0, so at 320px
                    wide the text column was squeezed to 3px, one letter per line. It now wraps beneath them instead. */}
                <div className="min-w-[9rem] flex-1">
                  {a.link ? (
                    <Link to={a.link} className="font-heading text-ele-strong hover:text-ele-green hover:underline">
                      {a.title}
                    </Link>
                  ) : (
                    <span className="font-heading text-ele-strong">{a.title}</span>
                  )}
                  {a.detail ? <div className="text-xs text-ele-body opacity-70">{a.detail}</div> : null}
                  {a.marginStatus ? (
                    <div className="mt-1">
                      <StatusPill
                        word={COST_FLOOR_TONE[a.marginStatus].word}
                        tone={COST_FLOOR_TONE[a.marginStatus].tone}
                        title="Advisory only: how this contract's annual value compares with the platform's internal cost floor for the lifts it covers."
                      />
                    </div>
                  ) : null}
                </div>
                {a.category === "quote_follow_up" ? (
                  <div className="flex shrink-0 items-center gap-2">
                    {a.link ? (
                      <Link to={a.link} className="font-menu text-xs tracking-wide text-ele-blue hover:underline">
                        View quote
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => markFollowedUp(a.id)}
                      disabled={snoozing.has(a.id)}
                      className="rounded-ele border border-ele-purple-accent px-2 py-1 font-menu text-[10px] tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong disabled:opacity-50"
                    >
                      {snoozing.has(a.id) ? "Saving..." : "Mark followed up"}
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          {total > items.length ? <p className="mt-2 text-xs text-ele-body opacity-60">Showing the top {items.length} of {total}.</p> : null}
        </>
      )}
    </Panel>
  );
}

function NpsPanel({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<NpsSummary | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    api.reporting
      .nps()
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const avgTone = data?.average == null ? "neutral" : data.average < 7 ? "alert" : "positive";
  return (
    <Panel
      title="Service satisfaction (NPS)"
      action={data ? <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">Last {data.windowDays} days</span> : null}
    >
      {error || data === null ? (
        <PanelStates error={error} loading={data === null} what="the NPS panel" />
      ) : data.responses === 0 ? (
        <p className="text-sm text-ele-body">No NPS responses in the last {data.windowDays} days yet.</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="Average score" value={data.average == null ? "No data" : `${data.average} / 10`} tone={avgTone} />
            <Tile label="NPS" value={data.npsScore == null ? "No data" : String(data.npsScore)} hint="promoters minus detractors" />
            <Tile
              label="Response rate"
              value={data.responseRate == null ? "No data" : `${Math.round(data.responseRate * 100)}%`}
              // "raised", not "sent" (Session AHY): the survey is drafted for the office to send, so
              // the denominator counts surveys raised, which is the most the platform can know.
              hint={`${data.responses} of ${data.sent} raised`}
            />
            <Tile label="Responses" value={String(data.responses)} />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">Score distribution</h3>
              <ul className="space-y-1 text-sm">
                <li className="flex justify-between gap-3">
                  <span className="text-ele-danger">Detractors (0 to 6)</span>
                  <span className="text-ele-body">{data.distribution.detractors}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-amber-400">Passives (7 to 8)</span>
                  <span className="text-ele-body">{data.distribution.passives}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-ele-green">Promoters (9 to 10)</span>
                  <span className="text-ele-body">{data.distribution.promoters}</span>
                </li>
              </ul>
            </div>
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">Recent responses</h3>
              {data.recent.length === 0 ? (
                <p className="text-sm text-ele-body opacity-60">None yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.recent.map((r, i) => (
                    <li key={i} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="text-ele-strong">{r.siteName ?? "Unknown site"}</span>
                        {r.comment ? <div className="text-xs text-ele-body opacity-70">{r.comment}</div> : null}
                      </div>
                      <span className={`shrink-0 font-heading ${r.score <= 6 ? "text-ele-danger" : r.score <= 8 ? "text-amber-400" : "text-ele-green"}`}>{r.score}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}

function PortfolioHealthPanelView({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<PortfolioHealthPanel | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    api.reporting
      .portfolioHealth()
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  return (
    <Panel title="Portfolio health" action={<UpdatedStamp iso={data?.generatedAt ?? null} />}>
      {error || data === null ? (
        <PanelStates error={error} loading={data === null} what="portfolio health" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <Tile label="Total lifts" value={String(data.totalLifts)} />
            <Tile label="Compliant (green)" value={String(data.green)} tone="positive" />
            <Tile label="Watch (amber)" value={String(data.amber)} />
            <Tile label="At risk (red)" value={String(data.red)} tone={data.red > 0 ? "alert" : "neutral"} />
            <Tile label="PPM overdue" value={String(data.ppmOverdue)} tone={data.ppmOverdue > 0 ? "alert" : "neutral"} />
            <Tile label="LOLER overdue" value={String(data.lolerOverdue)} tone={data.lolerOverdue > 0 ? "alert" : "neutral"} />
            <Tile
              label="PPM on time (12 months)"
              value={data.ppmCompliance.onTimePercent == null ? "Not enough history yet" : `${data.ppmCompliance.onTimePercent}%`}
              hint={`${data.ppmCompliance.onTime} on time, ${data.ppmCompliance.late} late, ${data.ppmCompliance.openOverdue} overdue`}
              tone={data.ppmCompliance.openOverdue > 0 ? "alert" : "positive"}
            />
            <Tile label="Renewals due (90 days)" value={String(data.renewals.count)} />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">LOLER examinations by status</h3>
              <ul className="space-y-1 text-sm">
                <li className="flex justify-between gap-3">
                  <span className="text-ele-green">Valid</span>
                  <span className="text-ele-body">{data.loler.valid} ({pct(data.loler.validPct)})</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-amber-400">Expiring (90 days)</span>
                  <span className="text-ele-body">{data.loler.expiring} ({pct(data.loler.expiringPct)})</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-ele-danger">Overdue</span>
                  <span className="text-ele-body">{data.loler.overdue} ({pct(data.loler.overduePct)})</span>
                </li>
              </ul>
            </div>
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">Renewals due within 90 days</h3>
              {data.renewals.list.length === 0 ? (
                <p className="text-sm text-ele-body opacity-60">No contracts renewing in the next 90 days.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.renewals.list.slice(0, 8).map((r) => (
                    <li key={r.id} className="flex justify-between gap-3">
                      <Link to="/office/renewals" className="text-ele-strong hover:text-ele-green hover:underline">
                        {r.customerName ?? "Customer"}
                        {r.siteName ? <span className="text-ele-body opacity-70"> | {r.siteName}</span> : null}
                      </Link>
                      <span className="text-ele-body">
                        {formatDate(r.dateIso)} ({r.daysUntil}d)
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {data.renewals.list.length > 8 ? <p className="mt-2 text-xs text-ele-body opacity-60">Showing 8 of {data.renewals.count}.</p> : null}
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}

function CommercialPanelView({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<CommercialPanel | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    api.reporting
      .commercial()
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  return (
    <Panel title="Commercial" action={<UpdatedStamp iso={data?.generatedAt ?? null} />}>
      {error || data === null ? (
        <PanelStates error={error} loading={data === null} what="the commercial panel" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <Tile label="ARR (active contracts)" value={gbp(data.arr)} tone="positive" hint="ex VAT" />
            <Tile label="Monthly recurring" value={gbp(data.mrr)} hint="ex VAT" />
            <Tile
              label="Quote conversion"
              value={data.quoteConversion.rate == null ? "No data" : pct(data.quoteConversion.rate)}
              hint={`${data.quoteConversion.signed} of ${data.quoteConversion.sent} sent, last ${data.quoteConversion.windowDays} days`}
            />
            <Tile label="Open pipeline" value={gbp(data.pipeline.openValue)} hint={`${data.pipeline.openCount} live prospects`} />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">Pipeline by stage</h3>
              <ul className="space-y-1 text-sm">
                {data.pipeline.byStage.map((s) => (
                  <li key={s.stage} className="flex justify-between gap-3">
                    <span className="text-ele-strong">{STAGE_LABEL[s.stage] ?? s.stage}</span>
                    <span className="text-ele-body">
                      {s.count} {s.count === 1 ? "lead" : "leads"} | {gbp(s.value)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">Top customers by contract value</h3>
              {data.topCustomers.length === 0 ? (
                <p className="text-sm text-ele-body opacity-60">No active contracts.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.topCustomers.map((c) => (
                    <li key={c.customerName} className="flex justify-between gap-3">
                      <span className="text-ele-strong">{c.customerName}</span>
                      <span className="text-ele-body">{gbp(c.value)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}

// Monthly revenue (Session AKF): folded in from the retired /office/reports page. Admin only,
// matching the underlying endpoint's own gate (server/src/modules/reports/report.routes.ts,
// `requireRole("admin")` on both GET /reports/monthly and /reports/monthly.pdf) -- not rendered
// at all for a non-admin office user, the same pattern Settings uses for its admin-only sections,
// rather than fetching and showing a "forbidden" message on a page everyone else can use freely.
function MonthlyRevenuePanel({ refreshKey }: { refreshKey: number }) {
  const [month, setMonth] = useState(currentReportMonth());
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    api.reports
      .monthly(month)
      .then((r) => {
        if (active) {
          setReport(r);
          setState("ready");
        }
      })
      .catch(() => {
        if (active) setState("error");
      });
    return () => {
      active = false;
    };
  }, [refreshKey, month]);

  const revenue = report?.figures.revenue;

  return (
    <Panel
      title="Monthly revenue"
      action={
        <div className="flex flex-wrap items-center gap-2">
          <label className="block">
            <span className="sr-only">Report month</span>
            <input
              type="month"
              value={month}
              max={currentReportMonth()}
              onChange={(e) => setMonth(e.target.value || currentReportMonth())}
              className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
            />
          </label>
          {state === "ready" && (
            <a
              href={api.reports.pdfHref(month)}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-ele bg-ele-green px-3 py-1.5 font-heading text-sm tracking-wide text-ele-bg transition hover:brightness-110"
            >
              Export as PDF
            </a>
          )}
        </div>
      }
    >
      {state !== "ready" ? (
        <PanelStates error={state === "error"} loading={state === "loading"} what="monthly revenue" />
      ) : (
        <Tile
          label="Revenue"
          value={revenue ? gbpExact(revenue.amount) : "-"}
          hint={revenue?.source === "xero" ? "Invoiced in Xero" : "Invoices due this month (cache)"}
          tone="positive"
        />
      )}
    </Panel>
  );
}

function OperationsPanelView({ refreshKey, from, to }: { refreshKey: number; from: string; to: string }) {
  const [data, setData] = useState<OperationsPanel | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    api.reporting
      .operations(from || undefined, to || undefined)
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey, from, to]);

  return (
    <Panel
      title="Operations"
      action={
        <div className="flex items-center gap-3">
          {data ? <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{data.window.label}</span> : null}
          <UpdatedStamp iso={data?.generatedAt ?? null} />
        </div>
      }
    >
      {error || data === null ? (
        <PanelStates error={error} loading={data === null} what="the operations panel" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {data.jobsByType.map((j) => (
              <Tile key={j.type} label={`${JOB_TYPE_LABEL[j.type] ?? j.type} completed`} value={String(j.count)} />
            ))}
            <Tile label="Call-out volume" value={String(data.callouts.volume)} />
            <Tile
              label="Avg response"
              value={data.callouts.avgResponseHours == null ? "No data" : `${data.callouts.avgResponseHours}h`}
              // The unmeasured count is shown, not hidden (Session ADQ): this average can only include
              // a call-out whose intake captured a real time of day, so without the count an office
              // manager cannot tell a genuinely fast month from one with almost no measurable data.
              // Session ATC (D18): the response is logged to an engineer ASSIGNED, never the arrival.
              hint={
                data.callouts.unmeasured > 0
                  ? `Logged to engineer assigned. ${data.callouts.assigned} measured, ${data.callouts.unmeasured} could not be measured`
                  : `Logged to engineer assigned. ${data.callouts.assigned} measured`
              }
            />
            <Tile
              label="LOLER defects"
              value={`${data.lolerDefects.open} open`}
              hint={`${data.lolerDefects.resolved} resolved`}
              tone={data.lolerDefects.open > 0 ? "alert" : "neutral"}
            />
            <Tile label="Active projects" value={String(data.projects.activeCount)} hint={`${gbp(data.projects.activeValue)} in progress`} />
          </div>
          {data.projects.byPhase.length > 0 ? (
            <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">Projects by phase</h3>
              <ul className="grid gap-1 text-sm sm:grid-cols-2">
                {data.projects.byPhase.map((p) => (
                  <li key={p.status} className="flex justify-between gap-3">
                    <span className="text-ele-strong">{titleCase(p.status)}</span>
                    <span className="text-ele-body">
                      {p.count} | {gbp(p.value)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}

// Pence -> "£x.xx" (job-cost figures are stored in pence).
function gbpPence(p: number): string {
  return `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Job-cost profitability (Session AK Item 2): average margin %, total labour + parts cost, and the
// highest/lowest-margin job for the period (shares the operations date window; default current month).
function ProfitabilityPanelView({ refreshKey, from, to }: { refreshKey: number; from: string; to: string }) {
  const [data, setData] = useState<ProfitabilityPanel | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    api.reporting
      .profitability(from || undefined, to || undefined)
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey, from, to]);

  return (
    <Panel
      title="Profitability"
      action={
        <div className="flex items-center gap-3">
          {data ? <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{data.window.label}</span> : null}
          <UpdatedStamp iso={data?.generatedAt ?? null} />
        </div>
      }
    >
      {error || data === null ? (
        <PanelStates error={error} loading={data === null} what="the profitability panel" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Session BDL (F3): the average margin and the labour total are built from engineers' cost rates (admin only), so the
                server leaves them out for anyone else. */}
            {data.avgMarginPercent !== undefined ? (
              <Tile
                label="Average margin"
                value={data.avgMarginPercent == null ? "Not recorded" : `${data.avgMarginPercent}%`}
                hint={`${data.jobsWithMargin} of ${data.jobCount} job${data.jobCount === 1 ? "" : "s"} priced`}
                tone={data.avgMarginPercent == null ? "neutral" : data.avgMarginPercent >= 0 ? "positive" : "alert"}
              />
            ) : null}
            {data.totalLabourCostPence !== undefined ? <Tile label="Total labour cost" value={gbpPence(data.totalLabourCostPence)} /> : null}
            <Tile label="Total parts cost" value={gbpPence(data.totalPartsCostPence)} />
            <Tile label="Jobs completed" value={String(data.jobCount)} />
            {/* Session ATE: approved engineer expenses, split job-linked (already folded into the
                margins above, via totalCostPence) from general (a claim with no job to attach to,
                reported here as its own monthly total rather than distorting any one job's margin). */}
            <Tile label="Job-linked expenses" value={gbpPence(data.totalOtherCostsPence)} hint={data.avgMarginPercent !== undefined ? "Already counted in the margins above" : undefined} />
            <Tile label="General expenses" value={gbpPence(data.generalExpensesTotalPence)} hint="Approved, no job to attach to; not in any margin" />
          </div>
          {(data.highest || data.lowest) && (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {data.highest && (
                <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                  <h3 className="mb-1 font-heading text-sm uppercase tracking-wide text-ele-green">Highest margin job</h3>
                  <div className="text-ele-strong">{data.highest.ref}</div>
                  <div className="text-xs text-ele-body opacity-70">{data.highest.siteName}</div>
                  <div className="mt-1 text-sm text-ele-green">
                    {gbpPence(data.highest.marginPence)}
                    {data.highest.marginPercent != null ? ` (${data.highest.marginPercent}%)` : ""}
                  </div>
                </div>
              )}
              {data.lowest && (
                <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                  <h3 className="mb-1 font-heading text-sm uppercase tracking-wide text-amber-400">Lowest margin job</h3>
                  <div className="text-ele-strong">{data.lowest.ref}</div>
                  <div className="text-xs text-ele-body opacity-70">{data.lowest.siteName}</div>
                  <div className={`mt-1 text-sm ${data.lowest.marginPence >= 0 ? "text-ele-body" : "text-ele-danger"}`}>
                    {gbpPence(data.lowest.marginPence)}
                    {data.lowest.marginPercent != null ? ` (${data.lowest.marginPercent}%)` : ""}
                  </div>
                </div>
              )}
            </div>
          )}
          {data.avgMarginPercent !== undefined ? (
            <p className="mt-3 text-xs text-ele-body opacity-60">
              Margin is the customer charge less the labour cost (time on site at the engineer cost rate) and parts. Jobs with no
              recorded charge or cost rate are not included in the margin figures.
            </p>
          ) : (
            <p className="mt-3 text-xs text-ele-body opacity-60">Margin and labour cost are shown to admins only.</p>
          )}
        </>
      )}
    </Panel>
  );
}

// Engineer utilisation (Session AK Item 5): a sortable per-engineer table (jobs, hours, type mix,
// NPS) over the operations date window (default current month).
type UtilSortKey =
  | "engineerName"
  | "jobsCompleted"
  | "totalHours"
  | "billableHours"
  | "nonBillableHours"
  | "avgHoursPerJob"
  | "ppm"
  | "callout"
  | "other"
  | "avgNps";

function ProfitabilityNumber(n: number | null, suffix = ""): string {
  return n == null ? "-" : `${n}${suffix}`;
}

function UtilisationPanelView({ refreshKey, from, to }: { refreshKey: number; from: string; to: string }) {
  const [data, setData] = useState<UtilisationPanel | null>(null);
  const [error, setError] = useState(false);
  const [sortKey, setSortKey] = useState<UtilSortKey>("jobsCompleted");
  const [dir, setDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    let active = true;
    setError(false);
    api.reporting
      .utilisation(from || undefined, to || undefined)
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey, from, to]);

  function toggleSort(key: UtilSortKey) {
    if (key === sortKey) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setDir(key === "engineerName" ? "asc" : "desc");
    }
  }

  const sorted = [...(data?.rows ?? [])].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    let cmp: number;
    if (typeof av === "string" && typeof bv === "string") cmp = av.localeCompare(bv);
    else cmp = (av == null ? -Infinity : (av as number)) - (bv == null ? -Infinity : (bv as number));
    return dir === "asc" ? cmp : -cmp;
  });

  // Session ACW Loop 6: Engineer / Jobs are the priority columns below md (who, and how much they
  // did); Hours, Avg/job, PPM, Call-out, Other and Avg NPS fold away. There is no per-engineer
  // detail page, so no chevron/row-navigation is added here (a BI table, not a navigable list).
  // Avg NPS joined the hidden set in the mobile truncation verification pass: this table has no
  // colgroup, so table-layout:fixed's no-colgroup fallback DOES reclaim a hidden column's space
  // (verified in a real browser: 3 visible columns split the table width equally, unlike the
  // colgroup-based tables elsewhere in the office, where a hidden column's share is never
  // reclaimed). But even with that reclaiming, 3 equal columns at 375px measured only 110px for
  // Engineer -- enough for "Adrian Baker" (exactly, no margin) but not "Mohammed Rahman" or
  // "Christopher Williams". Dropping to 2 columns measured 166px, comfortably fitting realistic
  // full names. A full MobileListCard rebuild was considered and rejected: this is a
  // comparison/stats grid the office scans across engineers, not a per-record list to tap into.
  const HIDDEN_ON_MOBILE: UtilSortKey[] = ["totalHours", "billableHours", "nonBillableHours", "avgHoursPerJob", "ppm", "callout", "other", "avgNps"];
  const columns: { key: UtilSortKey; label: string; align?: string }[] = [
    { key: "engineerName", label: "Engineer" },
    { key: "jobsCompleted", label: "Jobs", align: "text-right" },
    { key: "totalHours", label: "Hours", align: "text-right" },
    { key: "billableHours", label: "Billable", align: "text-right" },
    { key: "nonBillableHours", label: "Non-billable", align: "text-right" },
    { key: "avgHoursPerJob", label: "Avg/job", align: "text-right" },
    { key: "ppm", label: "PPM", align: "text-right" },
    { key: "callout", label: "Call-out", align: "text-right" },
    { key: "other", label: "Other", align: "text-right" },
    { key: "avgNps", label: "Avg NPS", align: "text-right" },
  ];
  const cell = (r: UtilisationRow, key: UtilSortKey): string => {
    switch (key) {
      case "engineerName": return r.engineerName;
      case "totalHours": return String(r.totalHours);
      case "avgHoursPerJob": return ProfitabilityNumber(r.avgHoursPerJob);
      case "avgNps": return ProfitabilityNumber(r.avgNps);
      default: return String(r[key]);
    }
  };

  return (
    <Panel
      title="Engineer utilisation"
      action={
        <div className="flex items-center gap-3">
          {data ? <span className="font-menu text-xs uppercase tracking-wide text-ele-body opacity-70">{data.window.label}</span> : null}
          <UpdatedStamp iso={data?.generatedAt ?? null} />
        </div>
      }
    >
      {error || data === null ? (
        <PanelStates error={error} loading={data === null} what="the utilisation panel" />
      ) : sorted.length === 0 ? (
        <p className="text-sm text-ele-body">No engineers to report on.</p>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={`px-3 py-2 ${c.align ?? ""} ${HIDDEN_ON_MOBILE.includes(c.key) ? "max-md:hidden" : ""}`}>
                    <button type="button" onClick={() => toggleSort(c.key)} className="font-menu tracking-widest hover:text-ele-strong">
                      {c.label}
                      {sortKey === c.key ? (dir === "asc" ? " ↑" : " ↓") : ""}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.engineerId} className="border-t border-ele-purple-accent">
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2 align-top ${c.align ?? ""} ${c.key === "engineerName" ? "text-ele-strong" : "text-ele-body"} ${HIDDEN_ON_MOBILE.includes(c.key) ? "max-md:hidden" : ""}`}
                    >
                      {cell(r, c.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function ComplianceSummaryPanelView({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<ComplianceSummaryPanel | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    api.reporting
      .complianceSummary()
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  return (
    <Panel title="Compliance" action={<UpdatedStamp iso={data?.generatedAt ?? null} />}>
      {error || data === null ? (
        <PanelStates error={error} loading={data === null} what="the compliance panel" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Link to="/office/personnel?tab=engineer" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 transition hover:border-ele-green">
              <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Engineer certs</div>
              <div className="mt-2 font-heading text-2xl text-ele-strong">
                {data.certs.expiringSoon} <span className="text-base text-ele-body">expiring</span>
              </div>
              <div className="mt-1 text-xs text-ele-body opacity-70">
                {data.certs.expired} expired{data.certs.expired > 0 ? ": action needed" : ""}
              </div>
            </Link>
            <Link to="/office/personnel?tab=engineer" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 transition hover:border-ele-green">
              <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Competence reviews</div>
              <div className="mt-2 font-heading text-2xl text-ele-strong">
                {data.competence.dueSoon} <span className="text-base text-ele-body">due soon</span>
              </div>
              <div className="mt-1 text-xs text-ele-body opacity-70">{data.competence.overdue} overdue</div>
            </Link>
            <Link to="/office/compliance" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 transition hover:border-ele-green">
              <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">LOLER examinations</div>
              <div className="mt-2 font-heading text-2xl text-ele-strong">
                {data.lolerExams.dueSoon} <span className="text-base text-ele-body">due soon</span>
              </div>
              <div className="mt-1 text-xs text-ele-body opacity-70">{data.lolerExams.overdue} overdue</div>
            </Link>
            <Link to="/office/subcontractors" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 transition hover:border-ele-green">
              <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Subcontractor docs</div>
              <div className="mt-2 font-heading text-2xl text-ele-strong">
                {data.subcontractorDocs.expiringSoon} <span className="text-base text-ele-body">expiring</span>
              </div>
              <div className="mt-1 text-xs text-ele-body opacity-70">
                {data.subcontractorDocs.expired} expired{data.subcontractorDocs.expired > 0 ? ": action needed" : ""}
              </div>
            </Link>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">Certs expiring within 60 days</h3>
              {data.certs.list.length === 0 ? (
                <p className="text-sm text-ele-body opacity-60">None.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.certs.list.slice(0, 8).map((c, i) => (
                    <li key={i} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-ele-strong">
                        {c.certType} <span className="text-ele-body opacity-70">({c.engineerName})</span>
                      </span>
                      <StatusPill className="shrink-0" domain="certificate" status={c.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">Competence reviews due</h3>
              {data.competence.list.length === 0 ? (
                <p className="text-sm text-ele-body opacity-60">All up to date.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.competence.list.slice(0, 8).map((r) => (
                    <li key={r.recordId} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-ele-strong">
                        {r.engineerName} <span className="text-ele-body opacity-70">| {r.competenceArea}</span>
                      </span>
                      <StatusPill className="shrink-0" domain="reviewDue" status={r.reviewStatus} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-body">LOLER examinations overdue</h3>
              {data.lolerExams.overdueList.length === 0 ? (
                <p className="text-sm text-ele-green">None overdue.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.lolerExams.overdueList.slice(0, 8).map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-ele-strong">
                        {a.ele ?? "a lift"} <span className="text-ele-body opacity-70">| {a.siteName ?? "site"}</span>
                      </span>
                      <span className="shrink-0 text-ele-danger">{a.daysOverdue}d</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Link to="/office/subcontractors" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 transition hover:border-ele-green">
              <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Labour-only spend</div>
              <div className="mt-2 font-heading text-2xl text-ele-strong">£{data.labourOnlySpend.toLocaleString("en-GB")}</div>
              <div className="mt-1 text-xs text-ele-body opacity-70">Declared spend across active labour-only subcontractors</div>
            </Link>
          </div>
        </>
      )}
    </Panel>
  );
}

// PPM compliance: of the scheduled service visits in the period, how many were completed on time
// (within the 14-day tolerance), late, missed, or are still upcoming. Its own from/to (defaulting to
// the current calendar year) + an optional site filter, independent of the operations window.
function PpmCompliancePanelView({ refreshKey }: { refreshKey: number }) {
  const year = new Date().getUTCFullYear();
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(yearEnd);
  const [siteId, setSiteId] = useState("");
  const [applied, setApplied] = useState({ from: yearStart, to: yearEnd, siteId: "" });
  const [data, setData] = useState<ReportingPpmComplianceResult | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    setData(null);
    api.reporting
      .ppmCompliance({ from: applied.from || undefined, to: applied.to || undefined, siteId: applied.siteId || undefined })
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [refreshKey, applied]);

  const pctInk = (p: number) => toneTextClass(p >= 90 ? "green" : p >= 60 ? "amber" : "red");

  return (
    <Panel title="PPM compliance" action={<UpdatedStamp iso={data?.generatedAt ?? null} />}>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong" />
        </label>
        <label className="block">
          <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong" />
        </label>
        <div className="block min-w-[min(100%,14rem)]">
          <RecordPicker kind="site" label="Site" value={siteId} placeholder="All sites (search to filter)" onChange={(id) => setSiteId(id)} />
        </div>
        <Button size="sm" onClick={() => setApplied({ from, to, siteId })}>
          Apply range
        </Button>
      </div>
      {error || data === null ? (
        <PanelStates error={error} loading={data === null} what="PPM compliance" />
      ) : data.onTimePercent === null ? (
        <>
          <p className="text-sm text-ele-body">Not enough history yet: no planned visit has been done, or has gone past its window, in this period.</p>
          {data.liftsNotAssessed.no_recorded_service > 0 ? (
            <p className="mt-2 text-sm text-ele-body">
              {data.liftsNotAssessed.no_recorded_service} lift{data.liftsNotAssessed.no_recorded_service === 1 ? "" : "s"} not assessed: no service recorded yet.
            </p>
          ) : null}
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-3">
            <span className={`font-heading text-5xl ${pctInk(data.onTimePercent)}`}>{data.onTimePercent}%</span>
            <span className="text-ele-body">on time</span>
          </div>
          <p className="mt-2 text-sm text-ele-body">
            {data.onTime} of {data.judged} planned visit{data.judged === 1 ? "" : "s"} done on time. The clock runs from the last completed visit; on time is the month it was due, or the allowed weeks either side.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="On time" value={String(data.onTime)} tone="positive" />
            <Tile label="Late" value={String(data.late)} />
            <Tile label="Open, overdue" value={String(data.openOverdue)} tone={data.openOverdue > 0 ? "alert" : "neutral"} />
            <Tile label="Open, not yet due" value={String(data.open - data.openOverdue)} />
          </div>
          {data.liftsNotAssessed.no_recorded_service > 0 ? (
            <p className="mt-3 text-sm text-ele-body">
              {data.liftsNotAssessed.no_recorded_service} lift{data.liftsNotAssessed.no_recorded_service === 1 ? "" : "s"} not assessed: no service recorded yet.
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}

// Session SC7B: every business tile the dashboard used to show, kept whole on this page. The dashboard now shows only the
// tiles that need attention (at most six); this panel is its "All figures" link, with the same tiles from the same code.
function AllFiguresPanel({ refreshKey }: { refreshKey: number }) {
  return (
    <Panel title="All figures">
      <KpiTileSet mode="all" refreshKey={refreshKey} />
    </Panel>
  );
}

export function ReportingDashboardPage() {
  const { user } = useAuth();
  // D57 (Session ARA): the monthly report is operational, not financial, so it opens to office
  // alongside admin -- matching the server's GET /api/reports/monthly(.pdf) guard.
  const canViewMonthlyRevenue = user?.role === "admin" || user?.role === "office";
  const [refreshKey, setRefreshKey] = useState(0);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");

  const refreshAll = useCallback(() => setRefreshKey((k) => k + 1), []);
  const applyWindow = useCallback(() => {
    setAppliedFrom(from);
    setAppliedTo(to);
  }, [from, to]);

  return (
    <div>
      <PageHeader
        className="mb-5"
        title="Business analytics"
        subtitle="The whole portfolio at a glance: health, commercials, operations and compliance, each from live data with its own last-updated time. The date range below applies to the operations figures; Refresh re-reads every panel."
        actions={
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong" />
          </label>
          <label className="block">
            <span className="mb-1 block font-menu text-[10px] uppercase tracking-wide text-ele-blue">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-sm text-ele-strong" />
          </label>
          <Button size="sm" onClick={applyWindow}>
            Apply
          </Button>
          <button
            onClick={refreshAll}
            className="rounded-ele border border-ele-purple-accent px-4 py-1.5 font-heading text-sm tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong"
          >
            Refresh
          </button>
        </div>
        }
      />

      <div className="space-y-6">
        <MorningActionsPanel refreshKey={refreshKey} />
        <AllFiguresPanel refreshKey={refreshKey} />
        <NpsPanel refreshKey={refreshKey} />
        <PortfolioHealthPanelView refreshKey={refreshKey} />
        <PpmCompliancePanelView refreshKey={refreshKey} />
        <CommercialPanelView refreshKey={refreshKey} />
        {canViewMonthlyRevenue && <MonthlyRevenuePanel refreshKey={refreshKey} />}
        <OperationsPanelView refreshKey={refreshKey} from={appliedFrom} to={appliedTo} />
        <ProfitabilityPanelView refreshKey={refreshKey} from={appliedFrom} to={appliedTo} />
        <UtilisationPanelView refreshKey={refreshKey} from={appliedFrom} to={appliedTo} />
        <ComplianceSummaryPanelView refreshKey={refreshKey} />
      </div>
    </div>
  );
}
